import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, Renderer2, ViewChild } from '@angular/core';

import { isEqual, isNil, union } from 'lodash';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subscription, combineLatest, filter, map, switchMap } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/naming-convention
import GPT3Tokenizer from 'gpt3-tokenizer';
import { DomHandler } from 'primeng/dom';


import { ChatMessageTypeModel, ChatRole } from '@api/ai/enums/AI-chat-completion.enum';
import { AIChatCompletionApiModel, AIChatCompletionMessageApiModel } from '@api/ai/model/AI-chat-completion-api.model';
import { EmailAccountConnectorApi } from '@api/emailAccountConnector/models/email-account-connector.api';
import { DataConnectorTypesEnum } from '@api/externalData/enums/data-connector-types.enum';

import { ConfigService } from '@Core/Config/services/config.service';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { DataConnectionService } from '@Core/Users/services/data-connection.service';
import { gateCall } from '@Core/rxjs-utils';

import { PanelComponent } from '@Common/Basic/components/Panel/panel.component';
import { AgprComponentStyleEnum } from '@Common/Basic/enums/social-analysis-types.enum';
import { AbstractAiDialogComponent } from '@Common/Dialogs/components/AbstractAIDialog/Abstract-AI-dialog.component';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import {
    FallbackPersonalizationsDialogComponent
} from '@Common/Dialogs/components/FallbackPersonalizationsDialog/fallback-personalizations-dialog.component';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { TextInputComponent } from '@Common/Forms/components/TextInput/text-input.component';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { ActionKeys } from '@Common/Forms/enums/keys.enum';
import { CustomDropdownOption, DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { validateEmail } from '@Common/Misc/helpers/formatting/email.helper';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { HeaderAssessmentMessage, HeaderEditorService } from '@Common/TargetingShared/services/header-editor.service';

import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { NewDataConnectionDialogComponent } from '@Account/components/NewDataConnectionDialog/new-data-connection-dialog.component';

import { mailMergeTagOptions } from '../../../../Core/models/merge-tag-options.const';
import { DistributionSendFrom } from '../../../models/distribution-send-from.model';
import { DistributionModel } from '../../../models/distribution.model';
import { DistributionPlainTextService } from '../PlainTextEditorDialog/plain-text.service';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';



export enum InfoType {
    fromName = 'fromName',
    replyEmail = 'replyEmail',
    subjectLine = 'subjectLine',
    previewText = 'previewText',
    sendFrom = 'sendFrom'
}

enum EmailIcons {
    invalid = 'fa-solid fa-circle-xmark',
    exclamation = 'fa-solid fa-circle-exclamation',
    check = 'fa-solid fa-circle-check'
}
enum AISuggestionTypes {
    subjectLine = 'subjectLine',
    previewText = 'previewText',
}

@Component({
    selector: 'agpr-ai-distribution-header-dialog',
    templateUrl: './AI-distribution-header-dialog.component.html',
    styleUrls: ['./AI-distribution-header-dialog.component.scss'],
    animations: [
        trigger('panelState', [
            state('hidden', style({
                opacity: 0,
                transform: 'scaleY(0)'
            })),
            state('visible', style({
                opacity: 1,
                transform: 'scaleY(1)'
            })),
            transition('visible => hidden', animate('.1s linear')),
            transition('hidden => visible', animate('.15s cubic-bezier(0, 0, 0.2, 1)'))
        ])
    ],
    standalone: false
})
export class AIDistributionHeaderDialogComponent extends AbstractAiDialogComponent implements AfterViewInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() openAiPermission: boolean;
    @Input() title: string = '';
    @Input() aiReferenceId: string;

    readonly minPanelWidth = 980;
    readonly maxPanelWidth = 1180;
    readonly infoTypes = InfoType;
    readonly buttonTypes = ButtonTypesEnum;

    hideSlideOverlay: boolean = false;

    loading: boolean = false;
    doneStreaming = true;
    hasGenerated: boolean = false;
    hasKeyMessage: boolean = false;
    disclaimerCollapsed: boolean = false;
    generatedContent: string = '';

    quickTipInfo = {
        header: '',
        title: '',
        hints: []
    };

    subscriptions: Subscription[] = [];

    distribution: DistributionModel;

    fromName: string = '';
    fromNameError: boolean = false;
    replyEmail: string = '';
    replyEmailError: boolean = false;
    subjectLine: string = '';
    subjectLineError: boolean = false;
    previewText: string = '';
    assessmentMessages: HeaderAssessmentMessage;

    emailIcon: EmailIcons | null = null;

    infoContent = {
        infoType: null,
        label: '',
        description: '',
        hints: [],
        callToActionLink: ''
    };

    bodyPlainText: string;

    tokenizer = new GPT3Tokenizer({ type: 'gpt3' });

    subjectLinePrompts: string[] = [];
    showSubjectLinePrompts: boolean = false;
    subjectLinePromptsLoading: boolean = false;
    subjectLineAiError: boolean = false;
    subjectLineSelected: number;
    previewTextPrompts: string[] = [];
    showPreviewTextPrompts: boolean = false;
    previewTextPromptsLoading: boolean = false;
    previewTextAiError: boolean = false;
    previewTextSelected: number;
    doneSettingBodyText: boolean = false;
    noBodyText: boolean = false;
    documentClickListener: any;

    aiQuickTipInfo: string = '';

    readonly maxTokenLimit = 1024;
    readonly subjectLineTemplateLength = 215;
    readonly subjectLineStringMatch: string = 'subjectLine';
    readonly previewTextStringMatch: string = 'pt';
    readonly mergeTagOptions = mailMergeTagOptions;
    mergeTagCustomOptions: CustomDropdownOption[] = [
        {
            label: 'FallbackPersonalizationDialog.heading',
            value: null,
            action: () => this.openEditMergeTag()
        }
    ];

    sendFromCallToAction: CustomDropdownOption = {
        label: 'Discover.targeting.distribution.headerEditor.sendFrom.callToAction',
        value: null,
        action: () => this.rhpCallToAction(InfoType.sendFrom)
    };

    @ViewChild('fromNameInput') fromNameInput: TextInputComponent;
    @ViewChild('replyEmailInput') replyEmailInput: TextInputComponent;
    @ViewChild('subjectLineInput') subjectLineInput: TextInputComponent;
    @ViewChild('previewTextInput') previewTextInput: TextInputComponent;

    @ViewChild('fromNameLiveRegion') fromNameLiveRegion: ElementRef;
    @ViewChild('replyEmailLiveRegion') replyEmailLiveRegion: ElementRef;
    @ViewChild('subjectLineLiveRegion') subjectLineLiveRegion: ElementRef;
    @ViewChild('previewTextLiveRegion') previewTextLiveRegion: ElementRef;

    @ViewChild('quickTipContainer') quickTipContainer: ElementRef;
    @ViewChild('panelDisclaimerToggle') public panelDisclaimerToggle: PanelComponent;

    emailAccountOptions: DropdownOptionModel<EmailAccountConnectorApi>[] = [];
    selectedDefaultEmailAccount: DropdownOptionModel<EmailAccountConnectorApi>;

    zIndex: number;

    constructor(
        coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public configService: ConfigService,
        public messageService: MessageService,
        public distributionService: DistributionService,
        public headerEditorService: HeaderEditorService,
        public renderer: Renderer2,
        private cd: ChangeDetectorRef,
        public plainTextService: DistributionPlainTextService,
        private userPreferencesService: UserPreferencesService,
        public dataConnectionService: DataConnectionService,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(coreDialogService, 'AISubjectLine');
        this.subscriptions.push(
            this.distributionService.getFocusedResource().pipe(
                filter(distribution => !!distribution)
            ).subscribe(distribution => {
                if (!this.distribution || !this.distribution.equals(distribution)) {
                    this.distribution = distribution;
                    this.bodyPlainText = this.plainTextService.generateBodyPlainText(this.distribution.blocks);

                    this.fromName = !!distribution.fromName ? '' + distribution.fromName : '';
                    this.replyEmail = !!distribution.replyEmail ? '' +  distribution.replyEmail : '';
                    this.subjectLine = !!distribution.subjectLine ? '' + distribution.subjectLine : '';
                    this.previewText = !!distribution.previewText ? '' + distribution.previewText : '';
                }
            }),
            this.headerEditorService.getHeaderAssessmentMessages().subscribe(assessmentMessages => {
                this.assessmentMessages = assessmentMessages;
            }),

            gateCall(this.dataConnectionService.currentUserHasEmailAccountConnectorAccessOnEnvironment()).pipe(
                switchMap(() => combineLatest([
                    this.dataConnectionService.emailAccountConnectors$,
                    this.distributionService.getFocusedResource().pipe(
                        filter(distribution => !!distribution)
                    ),
                    this.currentUserStateService.getUser()
                ]))
            ).subscribe(([connectors, distribution, user]) => {
                this.emailAccountOptions = [];
                this.emailAccountOptions.push({
                    label: this.localizationService.transform('Discover.userPreferences.defaultEmailAccount.system'),
                    value: null
                });
                connectors?.forEach(connector => {
                    if (connector.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.SHARED || connector.userId === user.id) {
                        this.emailAccountOptions.push({
                            label: connector.connectorName,
                            subLabel: connector.connectorEmail,
                            value: connector
                        });
                    }
                });

                if (distribution.sendFrom?.connectionId && connectors.some(connector => connector.id === distribution.sendFrom.connectionId) ) {
                    const index = this.emailAccountOptions.findIndex(x => x.value?.id === distribution.sendFrom.connectionId);
                    this.selectedDefaultEmailAccount = this.emailAccountOptions[index];
                } else {
                    this.selectedDefaultEmailAccount = this.emailAccountOptions[0];
                    // If we can't find the connectionId, we can assume it has expired
                    distribution.sendFrom = null;
                }
                this.validateAndAssesReplyEmail();
            })
        );
        this.bindDocumentClickListener();
    }

    /**
     * panelOpened
     * this is called when the animation for the panel is done opening
     * we need this because if we would call setMaxLimitOnBodyText before the animation, the panel would take "time" to open, and didn't look right
     * this way, the panel opens, and we have a loading indicator to show it.
     */
    panelOpened() {
        if (this.openAiPermission) {
            this.setMaxLimitOnBodyText();
            if (!this.tokenizer.encode(this.bodyPlainText)?.text?.length) {
                this.noBodyText = true;
            }
        } else {
            this.doneSettingBodyText = true;
        }
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.zIndex = DomHandler.zindex;
        });
    }

    /**
     * setMaxLimitOnBodyText
     * this function is to trim the distribution body text, if it's too long (we don't want to hit the max token limit)
     * it will loop through, and keep triming until the token limit is matched, currently it removes 500 characters from the end at a time.
     */
    setMaxLimitOnBodyText() {
        while (this.tokenizer.encode(this.bodyPlainText)?.text?.length > (this.maxTokenLimit - this.subjectLineTemplateLength)) {
            this.bodyPlainText = this.bodyPlainText.slice(0, -500);
        }
        this.doneSettingBodyText = true;
    }
    generate(type: string): void {
        this.generatedContent = '';
        this.loading = true;
        this.doneStreaming = false;

        this.fetchData(this.configService.getConfig().apiUrl + '/ai/chat/completions', type, this.aiReferenceId);
    }

    getRequestModel(type): AIChatCompletionApiModel {

        const message: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.HEADLINE_GENERATION,
            message : ''
        };

        if (type === AISuggestionTypes.subjectLine) {
            message.message = this.getSLRequestModel();
            return { messages: [message] };
        } else if (type === AISuggestionTypes.previewText) {
            message.message = this.getPTRequestModel();
            return { messages: [message] };
        }
    }
    onComplete(type: string){
        if (type === AISuggestionTypes.subjectLine) {
            return this.onSubjectLineComplete();
        } else if (type === AISuggestionTypes.previewText) {
            return this.onPreviewTextComplete();
        }
    }
    handleError(type: string) {
        if (type === AISuggestionTypes.subjectLine) {
            return this.handleSubjectLineError();
        } else if (type === AISuggestionTypes.previewText) {
            return this.handlePreviewTextError();
        }
    }

    //#region -> subject line
    generateSubjectLine() {
        this.showInfo(this.infoTypes.subjectLine);
        if (!this.subjectLinePromptsLoading && this.openAiPermission && !this.noBodyText) {
            document.getElementById('aiDistributionHeader.subjectLineInput')?.focus();
            this.subjectLineSelected = null;
            this.subjectLinePrompts = [];
            this.subjectLinePromptsLoading = true;
            this.showSubjectLinePrompts = true;
            this.subjectLineError = false;
            this.subjectLineAiError = false;
            this.previewTextAiError = false;
            this.showPreviewTextPrompts = false;
            this.generate(AISuggestionTypes.subjectLine);
        }
    }
    getSLRequestModel(): string {
        const message: any = {
            body: this.bodyPlainText,
            format: 'SUBJECT_LINE_EMAIL'
        };
        if (this.previewText) {
            message.previewText  = this.previewText;
        }
        return JSON.stringify(message);
    }
    onSubjectLineComplete(){
        for (let i = 1; i <= 3; i++) {
            // safari does not support lookbehind (?<=\/)
            // get data between <subjectLine1> & </subjectLine1>
            let slMatch = new RegExp(`(?:<${this.subjectLineStringMatch}${i}>).*?(?=\s*<\/${this.subjectLineStringMatch}${i}>)`, 'gs');
            let subjectLine = this.generatedContent.trim().match(slMatch);

            // sometimes the AI does not return the closing tags, so we will get all the data between <subjectLine1> & <subjectLine2>
            if (!subjectLine) {
                slMatch = new RegExp(`(?:<${this.subjectLineStringMatch}${i}>).*?(?=\s*<${this.subjectLineStringMatch}${i+1}>)`, 'gs');
                subjectLine = this.generatedContent.trim().match(slMatch);
            }

            // get data after <subjectLine3>
            if (!subjectLine && (i === 3)) {
                slMatch = new RegExp(`(?:<${this.subjectLineStringMatch}${i}>).*`, 'gs');
                subjectLine = this.generatedContent.trim().match(slMatch);
            }

            if (subjectLine?.length) {
                this.subjectLinePrompts = union(this.subjectLinePrompts, subjectLine);
                this.subjectLinePrompts.forEach((line, index) => {
                    this.subjectLinePrompts[index] = line.replace(`<${this.subjectLineStringMatch}${i}>`, '');
                });
            }
        }

        this.subjectLinePromptsLoading = false;
        (this.subjectLineLiveRegion.nativeElement as HTMLElement).textContent = this.localizationService.transform(
            this.subjectLinePrompts.length > 0 ? 'Common.input.suggestionCompleteMessage' : 'Common.input.noSuggestionGeneratedMessage'
        );
    }
    handleSubjectLineError() {
        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                detail: this.localizationService.transform('Common.dialog.aiDistributionHeader.subjectLineError')
            }
        );
        this.loading = false;
        this.hasGenerated = true;
        this.subjectLineAiError = true;
        this.subjectLinePromptsLoading = false;
        this.showSubjectLinePrompts = false;
    }
    setSubjectLine(suggestion) {
        this.subjectLine = suggestion;
        this.showSubjectLinePrompts = false;
        this.validateSubjectLine(null);
        this.subjectLineKeyUp(null);
    }
    //#endregion

    //#region -> preview text
    generatePreviewText() {
        this.showInfo(this.infoTypes.previewText);
        if (!this.previewTextPromptsLoading && this.openAiPermission && !this.noBodyText) {
            document.getElementById('aiDistributionHeader.previewTextInput')?.focus();
            this.previewTextSelected = null;
            this.previewTextPrompts = [];
            this.previewTextPromptsLoading = true;
            this.showPreviewTextPrompts = true;
            this.subjectLineAiError = false;
            this.previewTextAiError = false;
            this.showSubjectLinePrompts = false;
            this.generate(AISuggestionTypes.previewText);
        }
    }
    getPTRequestModel(): string {
        const message: any = {
            body: this.bodyPlainText,
            format: 'PREVIEW_TEXT_EMAIL'
        };
        if (this.subjectLine) {
            message.subjectLine  = this.subjectLine;
        }
        return JSON.stringify(message);
    }
    onPreviewTextComplete(){
        for (let i = 1; i <= 3; i++) {
            // Safari does not support lookbehind (?<=\/)
            // get data between <pt1> & </pt1>
            let ptMatch = new RegExp(`(?:<${this.previewTextStringMatch}${i}>).*?(?=\s*<\/${this.previewTextStringMatch}${i}>)`, 'gs');
            let previewText = this.generatedContent.trim().match(ptMatch);

            // sometimes the AI does not return the closing tags, so we will get all the data between <pt1> & <pt2>
            if (!previewText) {
                ptMatch = new RegExp(`(?:<${this.previewTextStringMatch}${i}>).*?(?=\s*<${this.previewTextStringMatch}${i+1}>)`, 'gs');
                previewText = this.generatedContent.trim().match(ptMatch);
            }

            // get data after <pt3>
            if (!previewText && (i === 3)) {
                ptMatch = new RegExp(`(?:<${this.previewTextStringMatch}${i}>).*`, 'gs');
                previewText = this.generatedContent.trim().match(ptMatch);
            }

            if (previewText?.length) {
                this.previewTextPrompts = union(this.previewTextPrompts, previewText);
                this.previewTextPrompts.forEach((line, index) => {
                    this.previewTextPrompts[index] = line.replace(`<${this.previewTextStringMatch}${i}>`, '');
                });
            }
        }

        this.previewTextPromptsLoading = false;
        (this.previewTextLiveRegion.nativeElement as HTMLElement).textContent = this.localizationService.transform(
            this.previewTextPrompts.length > 0 ? 'Common.input.suggestionCompleteMessage' : 'Common.input.noSuggestionGeneratedMessage'
        );
    }
    handlePreviewTextError() {
        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                detail: this.localizationService.transform('Common.dialog.aiDistributionHeader.previewTextError')
            }
        );
        this.loading = false;
        this.hasGenerated = true;
        this.previewTextAiError = true;
        this.previewTextPromptsLoading = false;
        this.showPreviewTextPrompts = false;
    }
    setPreviewText(suggestion) {
        this.previewText = suggestion;
        this.showPreviewTextPrompts = false;
        this.previewTextKeyUp(null);
    }
    //#endregion


    updateHeader() {
        this.distribution.fromName = this.fromName?.trim();
        this.distribution.replyEmail = this.replyEmail?.trim();
        this.distribution.subjectLine = this.subjectLine?.trim();
        this.distribution.previewText = this.previewText?.trim();

        this.headerEditorService.updateHeader(this.assessmentMessages);
        this.distributionService.headerUpdated();
        this.distributionService.updateDirtyFlag();
        this.closeDialog();
    }

    showInfo(infoType: InfoType) {
        this.infoContent.label = 'Discover.targeting.distribution.headerEditor.' + infoType;

        // The component could be made not to know the number of hints by using a prefix discovery system
        this.infoContent.hints = [];
        this.infoContent.infoType = infoType;
        this.infoContent.callToActionLink = null;
        switch (infoType) {
            case InfoType.fromName:
                for (let i = 1; i <= 5; i++) {
                    this.infoContent.hints.push('Discover.targeting.distribution.headerEditor.hints.fromName.' + i);
                }
                break;
            case InfoType.replyEmail:
                for (let i = 1; i <= 1; i++) {
                    this.infoContent.hints.push('Discover.targeting.distribution.headerEditor.hints.replyEmail.' + i);
                }
                break;
            case InfoType.subjectLine:
                for (let i = 1; i <= 4; i++) {
                    this.infoContent.hints.push('Discover.targeting.distribution.headerEditor.hints.subjectLine.' + i);
                }
                this.aiQuickTipInfo = 'Common.dialog.aiDistributionHeader.quickTip.subjectLine';
                break;
            case InfoType.previewText:
                for (let i = 1; i <= 4; i++) {
                    this.infoContent.hints.push('Discover.targeting.distribution.headerEditor.hints.previewText.' + i);
                }
                this.aiQuickTipInfo = 'Common.dialog.aiDistributionHeader.quickTip.previewText';
                break;
            case InfoType.sendFrom:
                this.infoContent.description = 'Discover.targeting.distribution.headerEditor.hints.sendFrom';
                this.infoContent.callToActionLink = 'Discover.targeting.distribution.headerEditor.sendFrom.callToAction';
                break;

        }
        this.setAssessmentMessageLiveRegion();
    }

    clearHints() {
        this.infoContent = {
            infoType: null,
            label: '',
            description: '',
            hints: [],
            callToActionLink: '',
        };
        this.aiQuickTipInfo = '';
    }

    subjectLineKeyUp(event: KeyboardEvent) {
        event?.stopImmediatePropagation();
        if (this.showSubjectLinePrompts) {
            if (!this.subjectLinePromptsLoading) {
                if (event?.key === ActionKeys.ENTER) {
                    if (!isNil(this.subjectLineSelected) && this.subjectLinePrompts?.length) {
                        this.setSubjectLine(this.subjectLinePrompts[this.subjectLineSelected]);
                    }
                }
                if (event?.key === ActionKeys.DOWN) {
                    if (isNil(this.subjectLineSelected) || (this.subjectLineSelected >= this.subjectLinePrompts.length - 1)) {
                        this.subjectLineSelected = 0;
                    } else {
                        this.subjectLineSelected = Math.min(this.subjectLineSelected + 1, this.subjectLinePrompts.length - 1);
                    }
                }
                if (event?.key === ActionKeys.UP) {
                    if (!this.subjectLineSelected) {
                        this.subjectLineSelected = this.subjectLinePrompts.length - 1;
                    } else {
                        this.subjectLineSelected = Math.max(this.subjectLineSelected - 1, 0);
                    }
                }
            }
        } else {
            if (event?.key === ActionKeys.ENTER || event?.key === ActionKeys.DOWN) {
                this.previewTextInput.giveFocus();
            }
        }
        this.assessmentMessages.subjectLine = this.headerEditorService.assessSubjectLine(this.subjectLine);
        // Ensure preview text (if entered) does not match new subject line
        this.assessmentMessages.previewText = this.headerEditorService.assessPreviewText(this.previewText, this.subjectLine);
        this.setAssessmentMessageLiveRegion();
        if (this.subjectLineAiError) {
            this.subjectLineAiError = !this.subjectLine;
        }
    }

    validateSubjectLine(event) {
        this.subjectLineError = !this.subjectLine;
        if (this.subjectLineAiError) {
            this.subjectLineAiError = !this.subjectLine;
        }
        const subjectLineButton = document.getElementById('agpr_aiDistributionHeader_generateSubjectLine')?.firstChild;
        if (!isEqual(subjectLineButton, event?.relatedTarget)) {
            this.clearHints();
        } else {
            document.getElementById('aiDistributionHeader.subjectLineInput').focus();
        }
    }

    validatePreviewText(event) {
        const previewTextButton = document.getElementById('agpr_aiDistributionHeader_generatePreviewText')?.firstChild;
        if (!isEqual(previewTextButton, event?.relatedTarget)) {
            this.clearHints();
        } else {
            document.getElementById('aiDistributionHeader.previewTextInput').focus();
        }
    }

    setSendFrom() {
        if (this.selectedDefaultEmailAccount?.value) {
            if (!this.distribution.sendFrom) {
                this.distribution.sendFrom = new DistributionSendFrom();
            }

            this.distribution.sendFrom.connectionId = this.selectedDefaultEmailAccount.value.id;
            this.distribution.sendFrom.connectionEmail = this.selectedDefaultEmailAccount.value.connectorEmail;
            this.distribution.sendFrom.connectionName = this.selectedDefaultEmailAccount.value.connectorName;
            this.distribution.sendFrom.grantId = this.selectedDefaultEmailAccount.value.grant?.id;
        } else {
            this.distribution.sendFrom = null;
        }

        const replyEmail = this.replyEmail?.trim();
        if (this.distribution.sendFrom && !replyEmail) {
            this.replyEmail = this.distribution.sendFrom.connectionEmail;
        } else if (replyEmail && this.distribution.sendFrom && this.distribution.sendFrom.connectionEmail.toLowerCase() !== replyEmail.toLowerCase()) {
            this.openSendFromConfirmDialog();
        }

        this.validateAndAssesReplyEmail();
    }

    previewTextKeyUp(event: KeyboardEvent) {
        event?.stopImmediatePropagation();
        if (this.showPreviewTextPrompts) {
            if (!this.previewTextPromptsLoading) {
                if (event?.key === ActionKeys.ENTER) {
                    if (!isNil(this.previewTextSelected) && this.previewTextPrompts?.length) {
                        this.setPreviewText(this.previewTextPrompts[this.previewTextSelected]);
                    }
                }
                if (event?.key === ActionKeys.DOWN) {
                    if (isNil(this.previewTextSelected) || (this.previewTextSelected >= this.previewTextPrompts.length - 1)) {
                        this.previewTextSelected = 0;
                    } else {
                        this.previewTextSelected = Math.min(this.previewTextSelected + 1, this.previewTextPrompts.length - 1);
                    }
                }
                if (event?.key === ActionKeys.UP) {
                    if (!this.previewTextSelected) {
                        this.previewTextSelected = this.previewTextPrompts.length - 1;
                    } else {
                        this.previewTextSelected = Math.max(this.previewTextSelected - 1, 0);
                    }
                }
            }
        } else {
            if (event?.key === ActionKeys.UP) {
                this.subjectLineInput.giveFocus();
            }
            if (event?.key === ActionKeys.ENTER || event?.key === ActionKeys.DOWN) {
                this.fromNameInput.giveFocus();
            }
            if (event?.key === ActionKeys.ENTER) {
                this.updateHeader();
            }
        }
        this.assessmentMessages.previewText = this.headerEditorService.assessPreviewText(this.previewText, this.subjectLine);
        this.setAssessmentMessageLiveRegion();

        if (this.previewTextAiError) {
            this.previewTextAiError = !this.previewText;
        }
    }

    fromNameKeyUp(event: KeyboardEvent) {
        if (event.key === ActionKeys.UP) {
            this.previewTextInput.giveFocus();
        }
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.DOWN) {
            this.replyEmailInput.giveFocus();
        }
        this.assessmentMessages.fromName = this.headerEditorService.assessFromName(this.fromName);
        this.setAssessmentMessageLiveRegion();
    }

    validateFromName() {
        this.fromNameError = !this.fromName;
        this.clearHints();
    }

    replyEmailKeyUp(event: KeyboardEvent) {
        if (event.key === ActionKeys.UP) {
            this.fromNameInput.giveFocus();
        }
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.DOWN) {
            this.updateHeader();
        }
        this.validateAndAssesReplyEmail();
    }

    replyEmailBlur() {
        this.validateReplyEmail();
        const replyEmail = this.replyEmail?.trim();
        if (this.distribution.sendFrom && !replyEmail) {
            this.replyEmail = this.distribution.sendFrom.connectionEmail;
        } else if (replyEmail && this.distribution.sendFrom && this.distribution.sendFrom.connectionEmail.toLowerCase() !== replyEmail.toLowerCase()) {
            this.openSendFromConfirmDialog();
        }
    }

    validateReplyEmail() {
        this.replyEmailError = !this.replyEmail;
        this.updateEmailErrorFlag();
    }

    setEmailIcon() {
        if (this.replyEmail && !validateEmail(this.replyEmail)) {
            this.emailIcon = EmailIcons.invalid;
        } else if (this.assessmentMessages.replyEmail.length > 0) {
            this.emailIcon = EmailIcons.exclamation;
        } else if (this.replyEmail) {
            this.emailIcon = EmailIcons.check;
        } else {
            this.emailIcon = null;
        }
    }

    updateEmailErrorFlag() {
        this.setEmailIcon();
        this.clearHints();
    }

    /**
     * For accessibility purposes: set text content of aria-live region so SR can read it properly
     */
    setAssessmentMessageLiveRegion() {
        const textContent = this.localizationService.transform('Common.input.showWarningMessage');
        // set to blank by default so that when text content changes, SR will announce it
        (this.previewTextLiveRegion.nativeElement as HTMLElement).textContent = '';
        (this.subjectLineLiveRegion.nativeElement as HTMLElement).textContent = '';
        (this.replyEmailLiveRegion.nativeElement as HTMLElement).textContent = '';
        (this.fromNameLiveRegion.nativeElement as HTMLElement).textContent = '';

        if (this.assessmentMessages.previewText.length > 0) {
            (this.previewTextLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
        if (this.assessmentMessages.subjectLine.length > 0) {
            (this.subjectLineLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
        if (this.assessmentMessages.replyEmail.length > 0 && this.emailIcon === EmailIcons.exclamation) {
            (this.replyEmailLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
        if (this.assessmentMessages.fromName.length > 0) {
            (this.fromNameLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
    }

    doNotCloseSubjectLine(event) {
        event.stopPropagation();
        this.showSubjectLinePrompts = true;
    }

    doNotClosePreviewText(event) {
        event.stopPropagation();
        this.showPreviewTextPrompts = true;
    }

    dismissPromptsByFocus(event: FocusEvent): void {
        if (!event.relatedTarget || !(event.relatedTarget as HTMLElement).classList.contains('agpr-aiDistributionHeader-suggestions-item')) {
            this.showSubjectLinePrompts = false;
            this.showPreviewTextPrompts = false;
        }
    }

    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', (event) => {
                const subjectLineInputClicked = isEqual(document.getElementById('aiDistributionHeader.subjectLineInput'), event?.target);
                const subjectLineLoadingClicked = isEqual(document.getElementById('agpr_aiDistributionHeader_subjectLineLoading'), event?.target);
                const generateSubjectLineClicked = isEqual(document.getElementById('agpr_aiDistributionHeader_generateSubjectLine'), event?.target);
                const previewTextInputClicked = isEqual(document.getElementById('aiDistributionHeader.previewTextInput'), event?.target);
                const previewTextLoadingClicked = isEqual(document.getElementById('agpr_aiDistributionHeader_previewTextLoading'), event?.target);
                const generatePreviewTextClicked = isEqual(document.getElementById('agpr_aiDistributionHeader_generatePreviewText'), event?.target);

                // forsome reason it is returning the body when I click on the generate AI buttons
                const generateClicked = isEqual(document.body, event?.target);

                if (!subjectLineInputClicked && !subjectLineLoadingClicked && !generateSubjectLineClicked && !generateClicked) {
                    this.showSubjectLinePrompts = false;
                }
                if (!previewTextInputClicked && !previewTextLoadingClicked && !generatePreviewTextClicked && !generateClicked) {
                    this.showPreviewTextPrompts = false;
                }
                this.cd.markForCheck();
            });
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    addMergeTagToInput(mergeTag: DropdownOptionModel, infoType: InfoType) {
        let inputElementId;

        if (infoType === InfoType.subjectLine) {
            inputElementId = 'aiDistributionHeader.subjectLineInput';
        } else if (infoType === InfoType.previewText) {
            inputElementId = 'aiDistributionHeader.previewTextInput';
        }

        const input = document.getElementById(inputElementId) as HTMLInputElement;
        if (input) {
            const startPos = input.selectionStart;
            const endPos = input.selectionEnd;

            // Insert merge tag at the cursor position
            if (infoType === InfoType.subjectLine) {
                this.subjectLine = input.value.substring(0, startPos) + mergeTag.value + input.value.substring(endPos, input.value.length);
            } else if (infoType === InfoType.previewText) {
                this.previewText = input.value.substring(0, startPos) + mergeTag.value + input.value.substring(endPos, input.value.length);
            }
            input.focus();
        }

        this.assessmentMessages.subjectLine = this.headerEditorService.assessSubjectLine(this.subjectLine);
        // Ensure preview text (if entered) does not match new subject line
        this.assessmentMessages.previewText = this.headerEditorService.assessPreviewText(this.previewText, this.subjectLine);
        this.setAssessmentMessageLiveRegion();
    }

    openEditMergeTag() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
            }
        };

        this.coreDialogService.open(FallbackPersonalizationsDialogComponent, io);
    }

    openDataConnectionsWindow() {
        this.dataConnectionService.openDataConnectionsNewWindow();
    }

    rhpCallToAction(infoType: InfoType) {
        if (infoType === InfoType.sendFrom) {
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );

            const io = {
                inputs: {
                    animationTrigger: animationTrigger,
                    overlayContainer: this.overlayContainer,
                    style: AgprComponentStyleEnum.social,
                    selectedConnectionType: DataConnectorTypesEnum.EMAIL_ACCOUNT
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        if (confirm) {

                        }
                    }
                }
            };

            this.coreDialogService.open(NewDataConnectionDialogComponent, io);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.unbindDocumentClickListener();
    }

    private openSendFromConfirmDialog() {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'distributionHeader.sendFromConfirmDialog',
                heading: 'Discover.targeting.distribution.header.sendFrom.popup.header',
                messageHtml: this.localizationService.transform('Discover.targeting.distribution.header.sendFrom.popup.text', {
                    address: this.selectedDefaultEmailAccount.value?.connectorEmail
                }),
                okLabel: 'Discover.targeting.distribution.header.sendFrom.popup.okay',
                style: AgprComponentStyleEnum.social,
                showCancel: true,
                cancelLabel: 'Discover.targeting.distribution.header.sendFrom.popup.cancel',
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.replyEmail = this.distribution.sendFrom.connectionEmail;
                        this.validateAndAssesReplyEmail();
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    private validateAndAssesReplyEmail() {
        this.validateReplyEmail();
        this.assessmentMessages.replyEmail = this.headerEditorService.assessReplyEmail(this.replyEmail);
        this.setEmailIcon();
    }
}
